import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import useChart from '../../hooks/useChart';
import usePatientDoc from '../../hooks/usePatientDoc';
import { PatientDoc } from '../../types/Patient/PatientDoc';
import { setInitialSelfGuidedNavObject } from '../../data/navigation/setInitialSelfGuidedNavObject';
import { db } from '../../constants/firebaseModular';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { getNextPage } from '../../data/navigation/getNextPage';
import { Chart } from '../../types/Chart/Chart';
import { NavInfo } from '../../types/Patient/NavInfo';
import { NavForward } from '../../components/buttons/NavForward';
import { Heading } from '../../components/Heading';
import { Menu } from 'iconoir-react';

export const Route = createFileRoute('/_authenticated/home')({
  component: Home,
});

function Home() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const { isFetched: patientInfoIsFetched } = patientDocQuery;
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'home',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  React.useEffect(() => {
    if (!patientInfo) {
      return;
    }
    // CHECK FOR NAV INFO OBJECT IN PATIENT DOC
    if (patientInfo && patientInfo.navInfo === undefined) {
      // IF NONE, SET TO INITIAL NAV INFO OBJECT
      const docRef = doc(db, 'users', patientId);
      updateDoc(docRef, {
        navInfo: {
          hasDoctor: false,
          hasHospital: false,
          hasAttorney: false,
          uploadingDocuments: true,
        } as NavInfo,
      });
    } else {
      // IF THEY HAVE A NAV INFO OBJECT, DO NOTHING. YOU DON'T WANT TO OVERWRITE THEIR PROGRESS.
    }
    // CHECK FOR NAV OBJECT IN PATIENT DOC
    if (patientInfoIsFetched && patientInfo && patientInfo.navObj === undefined) {
      // IF NONE, SET TO INITIAL SELF-GUIDED NAV OBJECT
      setInitialSelfGuidedNavObject(patientId);
    } else if (patientInfoIsFetched && patientInfo && patientInfo.navType.base === 'concierge') {
      // IF THEY PREVIOUSLY WERE A CONCIERGE PATIENT, DELETE THE NAV OBJECT AND NAV TYPE, SO THEY CAN BE RESET AND SET THEM
      const docRef = doc(db, 'users', patientId);
      // YOU HAVE TO DELETE THE NAV OBJECT AND NAV TYPE FIRST, OTHERWISE FIRESTORE SDK MERGES THE EXISTING CONCIERGE NAV OBJECT WITH THE SELF-GUIDED AND DOESN'T UPDATE THE NAV TYPE
      updateDoc(docRef, {
        navObj: deleteField(),
      })
        .then(() => {
          updateDoc(docRef, {
            navType: deleteField(),
          });
        })
        .then(() => {
          setInitialSelfGuidedNavObject(patientId);
        });
    } else {
      // IF THEY HAVE A NAV OBJECT, DO NOTHING. YOU DON'T WANT TO OVERWRITE THEIR PROGRESS.
    }
  }, [patientInfo, patientInfoIsFetched, patientId]);

  if (patientInfo && currentUser) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div className="flex flex-col items-center">
          <Heading id="welcome" textContent="Welcome!" />
          <p id="thank-you-message" className="text-xl">
            Thank your for creating your MIDEO account. You have made the first step in ensuring your wishes are heard
            even when you are unable to speak for yourself.
          </p>
          <div className="my-8 flex flex-col items-center">
            {/* TODO: Add captions track and disable warning */}
            {/* eslint-disable-next-line */}
            <video id="patient-education-video" className="w-1/3" controls>
              <source
                src="https://storage.googleapis.com/mideoid-console-public/videos/instructional/en/patientOverview.mp4"
                type="video/mp4"
              />
            </video>
          </div>
          <p className="text-xl">
            In order to make sure you are receiving the most accurate care we can provide we are going to ask you to
            complete a number of forms. We will break each of these topics into their own pages; however you will not
            see the full list in the{' '}
            <span>
              <Menu className="inline" />
            </span>{' '}
            &quot;Hamburger&quot; menu all at once. Rather, We will continue to add pages to this menu as you visit
            them. Feel free to return to any page at any point in the process. Clicking the Next button will always take
            you to the most recent page you need to complete.
          </p>
          {nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
        </div>
      </>
    );
  }
  return <div>Loading...</div>;
}
